<template>
    <v-card-text>
        <v-row dense align="center" class="mb-2">
            <v-col cols="12" md="8">
                <div class="text-body-2 grey--text text--darken-1">
                    Correos en HTML que recibe el participante. Los campos entre dobles llaves se sustituyen con los datos
                    del curso al enviar. La plantilla <b>certificado_disponible</b> es la que usa el envío automático.
                </div>
            </v-col>
            <v-col cols="12" md="4" class="text-md-right">
                <v-btn color="info" small outlined class="mr-2" :loading="cargando" @click="cargar"><v-icon small>refresh</v-icon></v-btn>
                <v-btn color="success" small outlined v-if="permisos.crear" @click="nueva"><v-icon small left>add</v-icon> Nueva plantilla</v-btn>
            </v-col>
        </v-row>

        <v-alert v-if="!correoHabilitado" dense text type="warning" class="mb-4">
            El correo saliente no está configurado en este servidor: se pueden editar y previsualizar las plantillas,
            pero los envíos quedarán pendientes hasta configurarlo.
        </v-alert>

        <v-data-table
            dense
            :headers="encabezados"
            :items="filas"
            :loading="cargando"
            item-key="plantilla_id"
            :items-per-page="15"
            :footer-props="$store.state.footerPropsTable"
        >
            <template v-slot:[`item.nombre`]="{ item }">
                <div class="font-weight-medium">{{ item.nombre }}</div>
                <code class="text-caption">{{ item.clave }}</code>
            </template>
            <template v-slot:[`item.activo`]="{ item }">
                <v-chip x-small label :color="item.activo ? 'success' : 'grey'" text-color="white">{{ item.activo ? 'Activa' : 'Inactiva' }}</v-chip>
            </template>
            <template v-slot:[`item.fecha_crea`]="{ item }">
                {{ (item.fecha_actualiza || item.fecha_crea) | fechaESPHora }}
                <div class="text-caption grey--text" v-if="item.registrado_por">por {{ item.registrado_por }}</div>
            </template>
            <template v-slot:[`item.acciones`]="{ item }">
                <v-btn icon small color="primary" title="Vista previa" @click="previsualizar(item)"><v-icon small>visibility</v-icon></v-btn>
                <v-btn icon small color="warning" v-if="permisos.crear" title="Enviar prueba" @click="abrirPrueba(item)"><v-icon small>forward_to_inbox</v-icon></v-btn>
                <v-btn icon small color="info" v-if="permisos.editar" title="Editar" @click="editar(item)"><v-icon small>edit</v-icon></v-btn>
                <v-btn icon small color="error" v-if="permisos.eliminar && !item.protegida" :disabled="item.en_uso > 0"
                       :title="item.en_uso > 0 ? 'Ya se usó en la cola: desactívela en lugar de eliminarla' : 'Eliminar'"
                       @click="confirmarEliminar(item)"><v-icon small>delete</v-icon></v-btn>
            </template>
            <template v-slot:no-data>
                <v-alert dense type="info" outlined class="text-center mt-3">No hay plantillas de correo.</v-alert>
            </template>
        </v-data-table>

        <!-- Editor -->
        <v-dialog v-model="dialogo" max-width="1100" persistent scrollable>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    {{ modelo.plantilla_id ? 'Editar' : 'Nueva' }} plantilla de correo
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="dialogo = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="form" v-model="valido" @submit.prevent="guardar">
                        <v-row dense>
                            <v-col cols="12" md="5">
                                <v-text-field v-model="modelo.nombre" label="Nombre" outlined dense maxlength="150" :rules="[reglas.requerido]"/>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field v-model="modelo.clave" label="Clave" outlined dense maxlength="60" :disabled="modelo.protegida"
                                              hint="Minúsculas, números y guion bajo" persistent-hint
                                              :rules="[reglas.requerido, reglas.clave]"/>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex align-center">
                                <v-switch v-model="modelo.activo" inset hide-details color="success" class="mt-0"
                                          :disabled="modelo.clave === 'certificado_disponible'"
                                          :label="modelo.activo ? 'Activa' : 'Inactiva'"/>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="modelo.asunto" label="Asunto" outlined dense maxlength="200" counter="200" :rules="[reglas.requerido]"/>
                            </v-col>
                            <v-col cols="12">
                                <div class="text-caption font-weight-bold grey--text text--darken-2 mb-1">Campos disponibles (clic para copiar)</div>
                                <v-chip v-for="(descripcion, clave) in campos" :key="clave" x-small label outlined color="primary" class="mr-1 mb-1"
                                        :title="descripcion" @click="copiar(clave)" v-text="'{{' + clave + '}}'"/>
                            </v-col>
                            <v-col cols="12" md="7">
                                <v-textarea v-model="modelo.html" label="Contenido HTML" outlined dense rows="18" no-resize class="editor-html"
                                            hint="Use tablas de 600 px y estilos en línea para que se vea bien en Outlook, Gmail y Hotmail. No se admite <script>."
                                            persistent-hint :rules="[reglas.requerido, reglas.sinScript]"/>
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-textarea v-model="modelo.texto_plano" label="Versión de texto (opcional)" outlined dense rows="18" no-resize class="editor-html"
                                            hint="Se envía junto al HTML para los clientes que no lo muestran. Si se deja vacía, se genera a partir del HTML."
                                            persistent-hint/>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text color="primary" @click="previsualizar(modelo)"><v-icon small left>visibility</v-icon> Vista previa</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogo = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="guardando" @click="guardar"><v-icon small left>save</v-icon> Guardar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Vista previa: el HTML se muestra en un iframe aislado (sandbox) con datos de ejemplo -->
        <v-dialog v-model="dialogoPrevia" max-width="760" scrollable>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold py-2">
                    Vista previa con datos de ejemplo
                    <v-spacer></v-spacer>
                    <v-btn-toggle v-model="vistaPrevia" dense mandatory color="primary" class="mr-3">
                        <v-btn small value="html">HTML</v-btn>
                        <v-btn small value="texto">Texto</v-btn>
                    </v-btn-toggle>
                    <v-btn icon small @click="dialogoPrevia = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-0">
                    <div class="px-4 py-2 text-body-2"><b>Asunto:</b> {{ previa.asunto }}</div>
                    <v-divider></v-divider>
                    <iframe v-if="vistaPrevia === 'html'" class="previa-correo" sandbox="" :srcdoc="previa.html" title="Vista previa del correo"></iframe>
                    <pre v-else class="previa-texto">{{ previa.texto }}</pre>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Eliminar -->
        <v-dialog v-model="dialogoEliminar" max-width="420">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Eliminar plantilla</v-card-title>
                <v-card-text>¿Eliminar la plantilla <b>{{ aEliminar ? aEliminar.nombre : '' }}</b>? Esta acción no se puede deshacer.</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogoEliminar = false">Cancelar</v-btn>
                    <v-btn color="error" depressed :loading="guardando" @click="eliminar">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Envío de prueba -->
        <v-dialog v-model="dialogoPrueba" max-width="460" persistent>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Enviar correo de prueba</v-card-title>
                <v-card-text>
                    <p class="text-body-2">Se envía <b>{{ prueba.nombre }}</b> con datos de ejemplo al correo indicado.</p>
                    <v-form ref="formPrueba" @submit.prevent="enviarPrueba">
                        <v-text-field v-model="prueba.correo" label="Correo de destino" outlined dense type="email" prepend-inner-icon="alternate_email"
                                      :rules="[reglas.requerido, reglas.correo]"/>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogoPrueba = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="enviando" @click="enviarPrueba"><v-icon small left>send</v-icon> Enviar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card-text>
</template>

<script>
/** Pestaña Plantillas: lista, editor, vista previa en iframe aislado y envío de prueba. */
export default {
    name: "NotificacionesPlantillasCorreo",
    data() {
        return {
            cargando: false,
            guardando: false,
            enviando: false,
            filas: [],
            campos: {},
            correoHabilitado: true,
            permisos: {crear: false, editar: false, eliminar: false},
            dialogo: false,
            dialogoPrevia: false,
            dialogoPrueba: false,
            dialogoEliminar: false,
            aEliminar: null,
            valido: true,
            vistaPrevia: "html",
            modelo: this.vacia(),
            previa: {asunto: "", html: "", texto: ""},
            prueba: {plantilla_id: null, nombre: "", correo: "", contenido: null},
            encabezados: [
                {text: "Plantilla", value: "nombre"},
                {text: "Asunto", value: "asunto"},
                {text: "Correos", value: "en_uso", align: "end"},
                {text: "Estado", value: "activo", sortable: false},
                {text: "Actualizada", value: "fecha_crea"},
                {text: "", value: "acciones", sortable: false, align: "end"},
            ],
            reglas: {
                requerido: v => (v !== null && v !== undefined && String(v).trim() !== "") || "Campo requerido",
                clave: v => /^[a-z0-9_]+$/.test(String(v || "")) || "Solo minúsculas, números y guion bajo",
                sinScript: v => !/<\s*script\b/i.test(String(v || "")) || "No se admite la etiqueta <script>",
                correo: v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim()) || "Correo no válido",
            },
        }
    },
    methods: {
        vacia() {
            return {plantilla_id: null, clave: "", nombre: "", asunto: "", html: "", texto_plano: "", activo: true, protegida: false};
        },
        cargar() {
            this.cargando = true;
            window.axios.get("/correos/plantillas").then(response => {
                if (response.data.success) {
                    this.filas = response.data.data.filas;
                    this.campos = response.data.data.campos;
                    this.correoHabilitado = response.data.data.correo_habilitado;
                    this.permisos = response.data.data.permisos;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudieron cargar las plantillas."))
                .finally(() => {
                    this.cargando = false;
                });
        },
        nueva() {
            this.modelo = this.vacia();
            this.dialogo = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        editar(fila) {
            this.modelo = {...this.vacia(), ...fila, texto_plano: fila.texto_plano || "", activo: !!fila.activo};
            this.dialogo = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        copiar(clave) {
            const texto = "{{" + clave + "}}";
            if (navigator.clipboard) {
                navigator.clipboard.writeText(texto).then(() => this.$toast.success(`Copiado: ${texto}`, {timeout: 1500})).catch(() => {});
            }
        },
        guardar() {
            if (!this.$refs.form.validate()) return;

            this.guardando = true;
            const datos = {
                plantilla_id: this.modelo.plantilla_id,
                clave: (this.modelo.clave || "").trim(),
                nombre: (this.modelo.nombre || "").trim(),
                asunto: (this.modelo.asunto || "").trim(),
                html: this.modelo.html,
                texto_plano: this.modelo.texto_plano || null,
                activo: this.modelo.activo ? 1 : 0,
            };

            window.axios.post("/correos/plantillas/guardar", datos).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.dialogo = false;
                    this.cargar();
                } else {
                    this.$toast.error(response.data.message, {timeout: 8000});
                }
            })
                .catch(error => this.$toast.error(this.mensajeError(error, "No se pudo guardar la plantilla."), {timeout: 8000}))
                .finally(() => {
                    this.guardando = false;
                });
        },
        // Datos que identifican el contenido: la plantilla guardada o lo que hay en el editor.
        contenidoDe(origen) {
            if (origen.plantilla_id && origen === this.filas.find(f => f.plantilla_id === origen.plantilla_id)) {
                return {plantilla_id: origen.plantilla_id};
            }
            return {asunto: origen.asunto, html: origen.html, texto_plano: origen.texto_plano || null};
        },
        previsualizar(origen) {
            if (!origen.html || !origen.asunto) {
                this.$toast.error("Escriba el asunto y el contenido HTML para ver la vista previa.");
                return;
            }

            window.axios.post("/correos/plantillas/vista_previa", this.contenidoDe(origen)).then(response => {
                if (response.data.success) {
                    this.previa = response.data.data;
                    this.vistaPrevia = "html";
                    this.dialogoPrevia = true;
                } else {
                    this.$toast.error(response.data.message, {timeout: 8000});
                }
            }).catch(error => this.$toast.error(this.mensajeError(error, "No se pudo generar la vista previa."), {timeout: 8000}));
        },
        abrirPrueba(fila) {
            this.prueba = {plantilla_id: fila.plantilla_id, nombre: fila.nombre, correo: this.prueba.correo || "", contenido: this.contenidoDe(fila)};
            this.dialogoPrueba = true;
            this.$nextTick(() => this.$refs.formPrueba && this.$refs.formPrueba.resetValidation());
        },
        enviarPrueba() {
            if (!this.$refs.formPrueba.validate()) return;

            this.enviando = true;
            window.axios.post("/correos/plantillas/prueba", {...this.prueba.contenido, correo: this.prueba.correo.trim()}).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message, {timeout: 8000});
                    this.dialogoPrueba = false;
                } else {
                    this.$toast.warning(response.data.message, {timeout: 10000});
                }
            })
                .catch(error => this.$toast.error(this.mensajeError(error, "No se pudo enviar el correo de prueba."), {timeout: 8000}))
                .finally(() => {
                    this.enviando = false;
                });
        },
        confirmarEliminar(fila) {
            this.aEliminar = fila;
            this.dialogoEliminar = true;
        },
        eliminar() {
            this.guardando = true;
            window.axios.post("/correos/plantillas/eliminar", {plantilla_id: this.aEliminar.plantilla_id}).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.dialogoEliminar = false;
                    this.cargar();
                } else {
                    this.$toast.error(response.data.message, {timeout: 8000});
                }
            })
                .catch(error => this.$toast.error(this.mensajeError(error, "No se pudo eliminar la plantilla."), {timeout: 8000}))
                .finally(() => {
                    this.guardando = false;
                });
        },
        mensajeError(error, defecto) {
            return (error.response && error.response.data && error.response.data.message) || defecto;
        },
    },
    created() {
        this.cargar();
    },
}
</script>

<style scoped>
.editor-html >>> textarea {
    font-family: Consolas, "Courier New", monospace;
    font-size: 12px;
    line-height: 1.4;
}

.previa-correo {
    display: block;
    width: 100%;
    height: 70vh;
    border: 0;
    background: #eef1f7;
}

.previa-texto {
    margin: 0;
    padding: 16px;
    white-space: pre-wrap;
    font-family: Consolas, "Courier New", monospace;
    font-size: 13px;
    max-height: 70vh;
    overflow: auto;
}
</style>
